import { createBrowserClient } from '@supabase/ssr';

import { Database } from '../database.types';
import { getSupabaseClientKeys } from '../get-supabase-client-keys';
import { withDbFailureReporting } from '../lib/db-failure-reporter';

/**
 * BAL-1144: browser calls report database failures through the same wrapper
 * as the server clients. The global `fetch` is read per call, not captured
 * once, so an instrumentation that patches it later (Sentry) still applies.
 */
function reportingFetchOptions() {
  return {
    fetch: withDbFailureReporting(
      (input, init) => globalThis.fetch(input, init),
      'browser',
    ),
  };
}

/**
 * Module-level singleton for the browser Supabase client.
 *
 * A browser tab must run exactly ONE GoTrueClient (and therefore one
 * auto-refresh timer). Multiple instances each start their own refresh
 * ticker; when several fire in the same second near token expiry they
 * stampede Supabase Auth's `/token` endpoint, tripping `429
 * over_request_rate_limit` and `Invalid Refresh Token: Already Used`
 * (refresh tokens rotate, so the loser of the race replays a spent
 * token). Prod audit logs showed 8-20 `token_refreshed`/second and dropped
 * sessions from exactly this pattern (BAL-752).
 *
 * `@supabase/ssr@0.12` already caches a browser singleton internally, but
 * that cache is a private module var on a third-party package (with a
 * `TODO(v3)`-adjacent lifecycle) and only holds if that package resolves to
 * a single module instance in the bundle. We own the guarantee here instead
 * of leasing it: one instance per tab, keyed to `@kit/supabase`, which has a
 * single resolution across the monorepo.
 */
let browserClient: ReturnType<typeof createBrowserClient<Database>> | undefined;

/**
 * Cross-tab refresh coordination is auth-js's lockless default, not a lock we
 * pass in (BALLEE-NX).
 *
 * BAL-752 armed `auth.lock = navigatorLock` as a cross-tab refresh mutex, and
 * BAL-792 / BALLEE-GP then had to wrap and feature-detect it because a custom
 * lock is the only path on which `_autoRefreshTokenTick` re-throws out of its
 * timer. Since auth-js 2.107 the default is lockless, and since 2.112.4 a
 * supplied `lock` logs a one-time `console.warn` that the option is deprecated
 * and removed in v3. `captureConsoleIntegration` turns that warning into one
 * Sentry event per page load (363 in 12h on 2.117.2).
 *
 * Dropping the lock keeps what BAL-752 needed (verified against the resolved
 * auth-js 2.117.2 source, `GoTrueClient.js` `_autoRefreshTokenTick` and
 * `_callRefreshToken`):
 * - One GoTrueClient per tab stays our job: the singleton below.
 * - Every tick re-reads the session from storage (cookies, shared across
 *   tabs), so once one tab rotates, the others see a fresh expiry and skip.
 * - Two tabs that do race both get the active token from GoTrue: a revoked
 *   token whose child is the active one returns that child
 *   (`internal/tokens/service.go`, parent-of-active), and the client's commit
 *   guard discards a stale write.
 */

/**
 * @name getSupabaseBrowserClient
 * @description Get a Supabase client for use in the Browser
 */
export function getSupabaseBrowserClient<GenericSchema = Database>() {
  // On the server there are no tabs and no auto-refresh timer to dedupe, so
  // never cache. Always hand back a fresh client.
  if (typeof window === 'undefined') {
    const keys = getSupabaseClientKeys();

    return createBrowserClient<Database>(keys.url, keys.publicKey, {
      global: reportingFetchOptions(),
    }) as unknown as ReturnType<typeof createBrowserClient<GenericSchema>>;
  }

  browserClient ??= instantiateBrowserClient();

  return browserClient as unknown as ReturnType<
    typeof createBrowserClient<GenericSchema>
  >;
}

function instantiateBrowserClient() {
  const keys = getSupabaseClientKeys();

  return createBrowserClient<Database>(keys.url, keys.publicKey, {
    global: reportingFetchOptions(),
  });
}
